import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { Job } from '../../projects/project-details/project-jobs/job.model';
import { User } from '../../core/shared-models/user.model';
import { Task, TaskJobInfo, TaskWorkingStatus } from '../task.model';
import { UtilsService } from '../../core/utils/utils.service';
import { AuthService } from '../../auth/auth.service';
import { DbService } from '../../core/db/db.service';
import { BasicJobInfo, ProjectService } from '../../projects/project.service';
import { NotificationService } from '../../core/notification/notification.service';
import { LoggerService } from '../../core/logger/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../settings/settings.service';
import { BaseSelectItem } from '../../core/select-drawer/base-select-item.interface';
import { SelectDrawerComponent } from '../../core/select-drawer/select-drawer.component';
import { TaskSharing } from '../task-sharing.model';
import { IonPopover, ModalController } from '@ionic/angular';
import { MatDatepicker } from '@angular/material/datepicker';
import moment from 'moment/moment';
import { lastValueFrom } from 'rxjs';
import { ServiceGroup } from '../../service-groups/service-group.model';
import { Project } from '../../projects/project.model';
import { SyncService } from '../../core/sync/sync.service';
import { TasksService } from '../tasks.service';
import { Router } from '@angular/router';
import { JobDetailsComponent } from '../../projects/project-details/project-jobs/job-details/job-details.component';

@Component({
    selector: 'app-task-details',
    templateUrl: './task-details.component.html',
    styleUrls: ['./task-details.component.scss'],
})
export class TaskDetailsComponent implements OnInit {
    @Input() projectView = false;
    @Output() taskCreated = new EventEmitter();
    taskQueueTotal = 0;
    taskQueueCurrent = 0;
    openedFromCalendarView = false;

    editTaskModalVisible = false;
    currentTaskFormGroup: FormGroup;
    currentAvailableUsers: User[] = [];
    currentTaskViewMode: 'edit' | 'view' = 'edit';
    currentTaskJobInfos: Array<Job> = [];
    currentTaskAvailableJobs: BasicJobInfo[] = [];
    currentTaskUserInfos: User[] = [];
    currentTaskResponsibleUserInfos: (User | string)[] = [];
    minTimestampDatePickerValue = null;
    maxTimestampDatePickerValue = null;
    drawerType: 'user' | 'responsible-users' | 'job';
    drawerItems: Array<BaseSelectItem> = [];
    drawerValue = undefined;
    existingTaskSharings: Map<string, TaskSharing> = new Map();

    projectModalVisible = false;
    selectedProjectName = '';

    disabledProjectSelection = false;

    projectDownloading = false;

    workingStatuses = {
        open: {
            color: 'danger',
            severity: 'danger',
        },
        started: {
            color: 'warning',
            severity: 'warn',
        },
        done: {
            color: 'success',
            severity: 'success',
        },
    };

    detailedJobModalVisible = false;
    detailedJobModalJobData = null;
    detailedJobModalServiceGroupData = null;
    detailedJobModalProjectData = null;
    detailedJobModalLoading = false;

    @ViewChild('statusPopover', { read: IonPopover })
    statusPopover!: IonPopover;

    private _drawerInstance;

    @ViewChild('selectDrawer', { static: false }) set selectDrawer(val: SelectDrawerComponent) {
        this._drawerInstance = val;
    }

    get selectDrawer(): SelectDrawerComponent {
        return this._drawerInstance;
    }

    /**
     * Get all sharings to delete (for use in html template)
     */
    get sharingsToDelete(): TaskSharing[] {
        if (!this.existingTaskSharings) {
            return [];
        }

        const existingUsers = this.currentAvailableUsers.map((u) => u.userName);
        const sharings: TaskSharing[] = [];

        for (const sharing of this.existingTaskSharings.values()) {
            const userName = sharing.sharedWith.replace('user#', '');
            if (!existingUsers.includes(userName)) {
                sharings.push(sharing);
            }
        }

        return sharings;
    }

    /**
     * Returns the current jobs (related to the task) form array
     */
    get currentTaskJobsFormArray(): AbstractControl[] {
        return (<FormArray>this.currentTaskFormGroup.get('jobs')).controls;
    }

    /**
     * Function which controls if the user can save the task (save button enabled or disabled)
     */
    get canSaveTask(): boolean {
        let canSave = true;
        if (
            this.currentTaskFormGroup.get('name').value == null ||
            this.currentTaskFormGroup.get('name').value.trim() === '' ||
            this.minTimestampDatePickerValue == null
        ) {
            canSave = false;
        }
        return canSave;
    }

    /**
     * Get drawer items for users (except all users that are allowed to see all tasks)
     */
    get userDrawerValue(): BaseSelectItem[] {
        return this.currentAvailableUsers
            .filter((x) => !this.canSeeAllTasks(x.licenseType))
            .map((user) => {
                return {
                    _id: user.userName,
                    name: this.displayUserInfos(user),
                    type: 'user',
                } as BaseSelectItem;
            });
    }

    /**
     * Get drawer items for responsible users
     */
    get responsibleUserDrawerValue(): BaseSelectItem[] {
        return this.currentAvailableUsers.map((user) => {
            return {
                _id: user.userName,
                name: this.displayUserInfos(user),
                type: 'user',
            } as BaseSelectItem;
        });
    }

    /**
     * Get drawer items for job selection
     */
    get jobDrawerValue(): BaseSelectItem[] {
        return this.currentTaskAvailableJobs.map((job) => {
            return {
                _id: job._id,
                name: job.jobNumber + ' ' + job.name,
                type: 'job',
            } as BaseSelectItem;
        });
    }

    constructor(
        public utils: UtilsService,
        public authSrv: AuthService,
        private db: DbService,
        private projectSrv: ProjectService,
        private notify: NotificationService,
        private logger: LoggerService,
        private translate: TranslateService,
        public settingsSrv: SettingsService,
        private syncSrv: SyncService,
        private taskSrv: TasksService,
        private router: Router,
        private cdRef: ChangeDetectorRef,
        private modalCtrl: ModalController
    ) {}

    ngOnInit() {
        this.currentAvailableUsers = this.authSrv.authInfo.companyUsers.sort(this.sortUserList);
    }

    /**
     * Opens the modal to create a new task
     */
    async openCreateTaskModal(
        calendarView = false,
        prefilledData: {
            name?: string;
            description?: string;
            startDate?: number;
            projectId?: string;
            projectName?: string;
            jobIds?: string[];
        } = null,
        step?: { current: number; total: number }
    ): Promise<void> {
        this.disabledProjectSelection = this.projectView;
        this.openedFromCalendarView = calendarView;
        this.taskQueueTotal = step?.total ?? 1;
        this.taskQueueCurrent = step?.current ?? 1;

        this.selectedProjectName = '';
        this.currentTaskViewMode = 'edit';
        this.currentTaskFormGroup = this.utils.objectToFormGroup(new Task());
        this.currentTaskJobInfos = [];

        // check if we have to prefill data from calendar page
        if (prefilledData != null) {
            this.currentTaskFormGroup.get('name').setValue(prefilledData.name);
            this.currentTaskFormGroup.get('description').setValue(prefilledData.description);

            if (prefilledData.projectId != null) {
                try {
                    // check if the project exists locally
                    let projectData = (await this.db.getItemById(prefilledData.projectId).toPromise()) as Project;

                    this.currentTaskFormGroup.get('relatedProject').setValue(prefilledData.projectId);
                    this.selectedProjectName = prefilledData.projectName ?? projectData.name;
                } catch (projectErr) {
                    if (projectErr === 'not_found') {
                        this.logger.warn('[TASKS PAGE] project not found (is not shared or deleted), prefilling with empty value');
                    } else {
                        this.logger.error('[TASKS PAGE] could not prefill project', projectErr);
                    }
                }
            }

            this.minTimestampDatePickerValue = moment(prefilledData.startDate).startOf('day');
            this.maxTimestampDatePickerValue = null;

            // handle prefilled job ids
            if (prefilledData.jobIds?.length > 0) {
                let allJobsFound = true;
                for (let jobId of prefilledData.jobIds) {
                    try {
                        let jobInfo = new Job(await this.db.getItemById(jobId).toPromise());
                        this.currentTaskJobInfos.push(jobInfo);

                        let jobInfoForTask = new TaskJobInfo();
                        jobInfoForTask.jobId = jobId;
                        jobInfoForTask.done = false;
                        (<FormArray>this.currentTaskFormGroup.get('jobs')).push(this.utils.objectToFormGroup(jobInfoForTask));
                    } catch (jobErr) {
                        if (jobErr === 'not_found') {
                            allJobsFound = false;
                            this.logger.warn(`[TASKS PAGE] job not found, skip auto selecting (job#${jobId})`);
                        } else {
                            this.logger.error('[TASKS PAGE] could not handle job for auto selecting', jobErr);
                        }
                    }
                }

                // check if a job is not found and the sync mode is limited -> notify the user
                if (!allJobsFound && this.syncSrv.syncMode$.getValue() === 'limited') {
                    this.notify.warn(this.translate.instant('tasks.details.info.sync-limited'), null, true, 10000);
                }
            }
        } else {
            this.minTimestampDatePickerValue = moment().startOf('day');
            this.maxTimestampDatePickerValue = null;
        }

        this.existingTaskSharings = new Map();
        this.currentTaskResponsibleUserInfos = [];
        this.currentTaskUserInfos = [this.authSrv.authInfo.userData].filter((x) => !this.canSeeAllTasks(x.licenseType));
        this.loadAvailableJobsBasedOnSelectedProject(this.currentTaskFormGroup.get('relatedProject').value);
        this.editTaskModalVisible = true;
        this.cdRef.detectChanges();
    }

    /**
     * Opens the modal to edit an existing task
     * @param task the task data
     * @param viewMode the view mode (edit or view)
     */
    async openEditTaskModal(task: Task, viewMode: 'edit' | 'view'): Promise<void> {
        this.currentTaskViewMode = viewMode;
        this.disabledProjectSelection = false;
        this.selectedProjectName = '';
        this.currentTaskFormGroup = this.utils.objectToFormGroup(new Task(task));

        if (!this.settingsSrv.currentUserRights.canManageTasks || this.currentTaskViewMode === 'view') {
            Object.keys(this.currentTaskFormGroup.controls).forEach((key) => {
                if (key !== 'jobs') {
                    this.currentTaskFormGroup.get(key)?.disable();
                }
            });
        }
        this.currentTaskJobInfos = [];
        this.minTimestampDatePickerValue = moment(task.startDate);
        this.maxTimestampDatePickerValue = task.deadlineDate != null ? moment(task.deadlineDate) : null;
        this.loadAvailableJobsBasedOnSelectedProject(this.currentTaskFormGroup.get('relatedProject').value);

        if (this.currentTaskFormGroup.get('relatedProject').value != null) {
            let projectData: Project;
            try {
                // check if the project exists locally (shared for user) --> otherwise download it manually
                projectData = (await this.db.getItemById(this.currentTaskFormGroup.get('relatedProject').value).toPromise()) as Project;
            } catch (projectErr) {
                if (projectErr === 'not_found') {
                    try {
                        this.logger.log('[TASKS PAGE] project of task not shared, downloading it manually');

                        // start sync, so the project data will be synced
                        this.projectDownloading = true;
                        await this.syncSrv.downloadProjectById(this.currentTaskFormGroup.get('relatedProject').value);
                        await this.syncSrv.downloadItemsByRelation('project#' + this.currentTaskFormGroup.get('relatedProject').value);

                        projectData = (await this.db
                            .getItemById(this.currentTaskFormGroup.get('relatedProject').value)
                            .toPromise()) as Project;

                        this.projectDownloading = false;

                        // trigger loading of project-list
                        this.taskSrv.loadProjectList$.emit();
                    } catch (err) {
                        this.projectDownloading = false;
                        this.logger.error('[TASKS PAGE] could not download project for task', err);
                        this.notify.error(this.translate.instant('tasks.details.error.project-loading'));
                    }
                }
            }

            // set the project name
            this.selectedProjectName = projectData?.name;
        }

        let hasDownloadedProjectData = false;
        for (let job of task.jobs) {
            let jobInfo: Job | null = null;

            try {
                jobInfo = new Job(await this.db.getItemById(job.jobId).toPromise());
            } catch (jobErr) {
                if (jobErr !== 'not_found') {
                    this.logger.error(`[TASKS PAGE] error while add job (${job.jobId}) to our list`, jobErr);
                    continue;
                }

                // job not found -> if its limited sync mode we download the project data once
                if (!hasDownloadedProjectData && this.syncSrv.syncMode$.getValue() === 'limited') {
                    this.projectDownloading = true;
                    hasDownloadedProjectData = true;

                    try {
                        await this.syncSrv.downloadItemsByRelation('project#' + this.currentTaskFormGroup.get('relatedProject').value);

                        this.projectDownloading = false;

                        // retry once again
                        try {
                            jobInfo = new Job(await this.db.getItemById(job.jobId).toPromise());
                        } catch (retryErr) {
                            if (retryErr !== 'not_found') {
                                this.logger.error(`[TASKS PAGE] error while add job (${job.jobId}) to our list after download`, retryErr);
                                continue;
                            }
                        }
                    } catch (syncModeErr) {
                        this.projectDownloading = false;
                        this.logger.error('[TASKS PAGE] could not download project data for task (limited sync)', syncModeErr);
                    }
                }
            }

            if (jobInfo) {
                this.currentTaskJobInfos.push(jobInfo);
            } else {
                // job not exists anymore -> remove it from our form array
                let jobFormArrayIndex = this.currentTaskJobsFormArray.findIndex((x) => x.value.jobId === job.jobId);
                if (jobFormArrayIndex > -1) {
                    (<FormArray>this.currentTaskFormGroup.get('jobs')).removeAt(jobFormArrayIndex);
                }

                this.logger.warn(`[TASKS PAGE] job not exists anymore --> remove it from our list (job#${job.jobId})`);
            }
        }

        // get task sharings from db
        let taskSharings: any = await this.db.getItemsByRelationAndType(`task#${task._id}`, 'task-sharing').toPromise();

        this.currentTaskResponsibleUserInfos = [];
        for (let responsibleUser of task.responsibleUsers) {
            let userName = typeof responsibleUser === 'string' ? responsibleUser : (responsibleUser as User).userName;
            let foundUser = this.currentAvailableUsers.find((x) => x.userName === userName);
            if (foundUser) {
                this.currentTaskResponsibleUserInfos.push(foundUser);
            } else {
                this.currentTaskResponsibleUserInfos.push(userName);
            }
        }

        this.existingTaskSharings = new Map();
        this.currentTaskUserInfos = [];

        // handle existing sharings
        for (let sharingItem of taskSharings) {
            let sharing = new TaskSharing(sharingItem);
            let userInfo = this.getUserInfoForTaskSharing(sharing);
            if (userInfo) {
                this.existingTaskSharings.set(userInfo.userName, sharing);
                this.currentTaskUserInfos.push(userInfo);
            } else {
                // user does not exist anymore --> should be deleted on save
                this.existingTaskSharings.set(`delete-sharing#${sharingItem._id}`, sharing);
            }
        }

        this.currentTaskUserInfos = this.currentTaskUserInfos.filter((x) => !this.canSeeAllTasks(x.licenseType)).sort(this.sortUserList);
        this.editTaskModalVisible = true;
    }

    /**
     * Returns the user info for given task sharing
     */
    getUserInfoForTaskSharing(taskSharing: TaskSharing) {
        return this.currentAvailableUsers.find((x) => x.userName == taskSharing.sharedWith.replace('user#', ''));
    }

    /**
     * Cancels current task editing
     */
    onCancelEditTask(): void {
        this.currentTaskFormGroup = null;
        this.editTaskModalVisible = false;
    }

    /**
     * Saves current edited task
     */
    async onSaveCurrentTask(): Promise<void> {
        let taskData = this.currentTaskFormGroup.getRawValue() as Task;
        taskData.startDate = this.minTimestampDatePickerValue.unix() * 1000;
        taskData.deadlineDate = this.maxTimestampDatePickerValue != null ? this.maxTimestampDatePickerValue.unix() * 1000 : null;

        // only add this confirmation if task status is done and the version is >-1 (a version of this task already exists)
        if (taskData.version > -1 && taskData.workingStatus === TaskWorkingStatus.DONE) {
            try {
                let shouldMarkTaskAsDone: any = await this.notify.tripleConfirm(
                    this.translate.instant('tasks.details.info.done-info-msg'),
                    this.translate.instant('tasks.details.info.done-info-title'),
                    this.translate.instant('tasks.details.info.done-info-confirm'),
                    this.translate.instant('tasks.details.info.done-info-deny'),
                    this.translate.instant('shared.cancel')
                );

                // check if the user wants to mark the task as done (if not --> status is set so started)
                if (!shouldMarkTaskAsDone) {
                    taskData.workingStatus = TaskWorkingStatus.STARTED;
                }
            } catch (_) {
                this.logger.log('[TASKS PAGE] user cancelled confirmation', _);
                return;
            }
        }

        // add sharing if needed
        if (this.currentTaskUserInfos.length < 1 && !this.canSeeAllTasks(this.authSrv?.authInfo?.userData?.licenseType)) {
            this.currentTaskUserInfos = [this.authSrv.authInfo.userData];
        }

        // status handling
        if (taskData.workingStatus === TaskWorkingStatus.STARTED && (taskData.startedAt == null || taskData.startedAt === 0)) {
            taskData.startedAt = Date.now();
            taskData.startedBy = this.authSrv.authInfo.userData.userName;
        } else if (taskData.workingStatus === TaskWorkingStatus.DONE && (taskData.completedAt == null || taskData.completedAt === 0)) {
            taskData.completedAt = Date.now();
            taskData.completedBy = this.authSrv.authInfo.userData.userName;

            // if the task is directly completed (if only less than 2 jobs exist) --> set startedAt to completedAt
            if (taskData.startedAt == null || taskData.startedAt === 0) {
                taskData.startedAt = taskData.completedAt;
                taskData.startedBy = taskData.completedBy;
            }
        }

        const currentUsersSharings = new Set(this.currentTaskUserInfos.map((u) => u.userName));
        const existingUserSharings = new Set(Array.from(this.existingTaskSharings.keys()).filter((k) => !k.startsWith('delete-sharing#')));

        let sharingsToCreate: TaskSharing[] = [];
        for (let selectedUser of this.currentTaskUserInfos) {
            if (!existingUserSharings.has(selectedUser.userName)) {
                let newTaskSharing = new TaskSharing();
                newTaskSharing.taskId = taskData._id;
                newTaskSharing.sharedWith = `user#${selectedUser.userName}`;
                newTaskSharing.relation = `task#${taskData._id}`;

                sharingsToCreate.push(newTaskSharing);
            }
        }

        // check if we have to delete some sharings
        const sharingsToDelete: TaskSharing[] = [];
        for (const [key, sharing] of this.existingTaskSharings.entries()) {
            const userName = key.replace('delete-sharing#', '');

            if (!currentUsersSharings.has(userName)) {
                sharingsToDelete.push(sharing);
            }
        }

        // handle sharings which should be deleted
        for (let sharingToDelete of sharingsToDelete) {
            try {
                let deleteResponse = await this.db.delete(sharingToDelete._id).toPromise();
                this.logger.log('successfully deleted sharing item', deleteResponse);
            } catch (error) {
                this.logger.error('could not delete sharing item', error);
                this.notify.error(this.translate.instant('tasks.details.error.sharing-not-deleted'));
                this.utils.sentryCaptureException(error);
            }
        }

        // handle sharings which should be created
        for (let sharingToCreate of sharingsToCreate) {
            try {
                let createResponse = await this.db.put(sharingToCreate).toPromise();
                this.logger.log('successfully created sharing item', createResponse);
            } catch (error) {
                this.logger.error('could not create sharing item', error);
                this.notify.error(this.translate.instant('tasks.details.error.sharing-not-created'));
                this.utils.sentryCaptureException(error);
            }
        }

        // handle responsible users
        (<UntypedFormArray>this.currentTaskFormGroup.get('responsibleUsers')).clear();
        for (let userInfo of this.currentTaskResponsibleUserInfos) {
            if (typeof userInfo === 'string') {
                // if type is string it means the user does not exist anymore --> we only have the username as userInfo
                (<UntypedFormArray>this.currentTaskFormGroup.get('responsibleUsers')).push(new UntypedFormControl(userInfo));
            } else {
                (<UntypedFormArray>this.currentTaskFormGroup.get('responsibleUsers')).push(new UntypedFormControl(userInfo?.userName));
            }
        }
        taskData.responsibleUsers = this.currentTaskFormGroup.get('responsibleUsers').getRawValue();

        try {
            // save task to db
            await this.db.put(taskData).toPromise();
            this.notify.success(this.translate.instant('tasks.details.info.task-saved'));
            this.logger.log('successfully saved task');
        } catch (error) {
            this.logger.error('could not save task', error);
            this.notify.error(this.translate.instant('tasks.details.error.task-not-saved'));
            this.utils.sentryCaptureException(error);
        }

        this.editTaskModalVisible = false;
        this.cdRef.detectChanges();

        // emit that the task is created
        this.taskCreated.emit();

        // if we are in project view and the guest access is enabled, we need to check if there are still relevant tasks in the project
        // if we are not in project view, we need to check if the user has access to the project (project-sharings)
        try {
            if (
                (taskData.relatedProject != null && this.projectView && this.projectSrv.currentProjectGuestAccess) ||
                (taskData.relatedProject != null && !this.projectView && !(await this.projectSrv.hasProjectAccess(taskData.relatedProject)))
            ) {
                const tasks: Task[] = (await this.db.getItemsByType('task').toPromise()) as Task[];
                const hasRelevantTasks = tasks.some(
                    (x) =>
                        x.relatedProject === taskData.relatedProject &&
                        (this.settingsSrv.getUserRights().canManageTasks || x.workingStatus !== 'done')
                );

                // check if there are relevant tasks in the project (otherwise -> navigate user to dashboard)
                if (!hasRelevantTasks) {
                    if (this.projectView) {
                        // navigate user to the dashboard and set the current project to null
                        await this.router.navigate(['/'], { replaceUrl: true });
                        this.projectSrv.setCurrentProject(null);

                        this.notify.success(this.translate.instant('tasks.details.info.all-tasks-done'), null, true, 4000);
                    }

                    // remove the project from our list
                    await this.syncSrv.deleteProjectData(taskData.relatedProject, true);
                }
            }
        } catch (checkProjectTasksErr) {
            this.logger.error('[TASKS PAGE] failed: check and handling for open tasks in the project', checkProjectTasksErr);
            this.utils.sentryCaptureMessage('failed: check and handling for open tasks in the project', checkProjectTasksErr);
        }
    }

    /**
     * If the task project id changes we need to load available jobs for selected project
     */
    onCurrentTaskProjectIdChanged($event: Project): void {
        this.currentTaskFormGroup.get('relatedProject').setValue($event._id);
        this.selectedProjectName = $event.name;

        // clear current jobs
        (<FormArray>this.currentTaskFormGroup.get('jobs')).clear();
        this.currentTaskJobInfos = [];

        if ($event._id != null) {
            this.loadAvailableJobsBasedOnSelectedProject($event._id);
        }

        this.projectModalVisible = false;
    }

    /**
     * Loads available jobs based on current task projectId
     * @param projectId
     */
    loadAvailableJobsBasedOnSelectedProject(projectId: string): void {
        this.currentTaskAvailableJobs = []; // reset
        this.projectSrv.getJobsWithBasicInfo({ projectId: projectId }).subscribe(
            (res) => {
                this.currentTaskAvailableJobs = res.sort(this.sortJobList);
            },
            (err) => {
                this.logger.error('[TASKS] could not load jobs for project', err);
            }
        );
    }

    /**
     * Toggle drawer if needed
     * @param type the type of the drawer (user, responsible-users or job)
     * @param event the click event
     */
    maybeToggleDrawer(type: 'user' | 'responsible-users' | 'job', event: any) {
        event.preventDefault();

        // set type and check for it
        this.drawerType = type;

        if (this.drawerType === 'user') {
            this.drawerItems = this.userDrawerValue;
            this.drawerValue = this.drawerItems.filter((i) => this.currentTaskUserInfos.some((x) => x.userName === i._id)) || [];
        } else if (this.drawerType === 'responsible-users') {
            this.drawerItems = this.responsibleUserDrawerValue;
            this.drawerValue =
                this.drawerItems.filter((i) =>
                    this.currentTaskResponsibleUserInfos.some((x) => (typeof x === 'string' ? x === i._id : x.userName === i._id))
                ) || [];
        } else {
            this.drawerItems = this.jobDrawerValue;
            this.drawerValue = this.drawerItems.filter((i) => this.currentTaskJobInfos.some((x) => x._id === i._id)) || [];
        }

        // open or render and then open the drawer
        if (this.selectDrawer) {
            this.selectDrawer.toggleDrawer();
        }
    }

    /**
     * Triggered when jobs get added to our current task
     * @param selectionMinimalInfo
     */
    async onCurrentTaskDrawerItemsChanged(selectionMinimalInfo: BaseSelectItem | BaseSelectItem[]): Promise<void> {
        const items = Array.isArray(selectionMinimalInfo) ? selectionMinimalInfo : [selectionMinimalInfo];
        let itemIds = items.map((x) => x._id);

        if (this.drawerType === 'user') {
            let selectedUsers = [];
            for (let userName of itemIds) {
                let foundUser = this.currentAvailableUsers.find((x) => x.userName === userName);
                if (foundUser) {
                    selectedUsers.push(foundUser);
                }
            }

            this.currentTaskUserInfos = selectedUsers.sort(this.sortUserList);

            // handle sharing users for "responsible users"
            const currentTaskUserNames = new Set(this.currentTaskUserInfos.map((u) => u.userName));
            this.currentTaskResponsibleUserInfos = this.currentTaskResponsibleUserInfos.filter((x) => {
                const name = typeof x === 'string' ? x : x.userName;

                let foundUser = this.currentAvailableUsers.find((x) => x.userName === name);
                let canSeeAllTasks = foundUser ? this.canSeeAllTasks(foundUser?.licenseType) : false;

                return canSeeAllTasks || currentTaskUserNames.has(name);
            });
        } else if (this.drawerType === 'responsible-users') {
            let selectedUsers = [];
            for (let userName of itemIds) {
                let foundUser: User = this.currentAvailableUsers.find((x) => x.userName === userName);
                if (foundUser) {
                    selectedUsers.push(foundUser);
                } else {
                    selectedUsers.push(userName);
                }
            }
            this.currentTaskResponsibleUserInfos = selectedUsers.sort(this.sortUserList);

            // handle responsible users for "sharing users"
            for (let userInfo of this.currentTaskResponsibleUserInfos) {
                let userInCurrentTaskUserInfos = this.currentTaskUserInfos.find(
                    (x) => x.userName === (typeof userInfo === 'string' ? userInfo : userInfo.userName)
                );
                if (userInCurrentTaskUserInfos == null) {
                    let foundUser = this.currentAvailableUsers.find(
                        (x) => x.userName === (typeof userInfo === 'string' ? userInfo : userInfo.userName)
                    );
                    if (foundUser && !this.canSeeAllTasks(foundUser?.licenseType)) {
                        this.currentTaskUserInfos.push(foundUser);
                    }
                }
            }
            this.currentTaskUserInfos.sort(this.sortUserList);
        } else {
            const selectedJobIds = new Set(itemIds);
            const jobsFormArray = <FormArray>this.currentTaskFormGroup.get('jobs');

            // Remove deselected jobs from the FormArray (iterating backwards to keep indices stable)
            for (let i = jobsFormArray.length - 1; i >= 0; i--) {
                const jobId = jobsFormArray.at(i).get('jobId')?.value;
                if (!selectedJobIds.has(jobId)) {
                    jobsFormArray.removeAt(i);
                }
            }

            // Remove deselected jobs from the visible job infos
            this.currentTaskJobInfos = this.currentTaskJobInfos.filter((j) => selectedJobIds.has(j._id));

            // Add newly selected jobs, skip duplicates
            const existingJobIds = new Set(jobsFormArray.controls.map((c) => c.get('jobId')?.value));
            for (let jobId of itemIds) {
                if (existingJobIds.has(jobId)) continue;

                // get job info from db
                let jobInfo = new Job(await this.db.getItemById(jobId).toPromise());

                // add job id to our task data
                let jobInfoForTask = new TaskJobInfo();
                jobInfoForTask.jobId = jobInfo._id;
                jobInfoForTask.done = false;
                jobsFormArray.push(this.utils.objectToFormGroup(jobInfoForTask));

                // add job info to our visible job infos array
                this.currentTaskJobInfos.push(jobInfo);
            }

            this.currentTaskJobInfos.sort(this.sortJobList);
            this.sortJobsFormArray();
        }
    }

    /**
     * Sort jobs form array
     */
    sortJobsFormArray(): void {
        const jobsFormArray = <FormArray>this.currentTaskFormGroup.get('jobs');
        const controls = jobsFormArray.controls.slice().sort((a, b) => {
            const jobA = this.currentTaskJobInfos.find((j) => j._id === a.get('jobId')?.value);
            const jobB = this.currentTaskJobInfos.find((j) => j._id === b.get('jobId')?.value);
            return this.sortJobList(jobA, jobB);
        });

        jobsFormArray.clear();
        controls.forEach((c) => jobsFormArray.push(c));
    }

    /**
     * Triggered when a job gets removed from our current task
     * @param index
     */
    async onJobRemovedFromCurrentTask(index: number): Promise<void> {
        (<FormArray>this.currentTaskFormGroup.get('jobs')).removeAt(index);

        this.currentTaskJobInfos.splice(index, 1);
    }

    /**
     * Triggered when a user gets removed from our current task
     * @param index
     */
    onUserRemovedFromCurrentTask(index: number): void {
        let userInfo = this.currentTaskUserInfos[index];

        // remove element from our list
        this.currentTaskUserInfos.splice(index, 1);

        // remove user also from our responsible users
        let responsibleUserIndex = this.currentTaskResponsibleUserInfos.findIndex((x) =>
            typeof x === 'string' ? x === userInfo.userName : x.userName === userInfo.userName
        );
        if (responsibleUserIndex > -1) {
            this.currentTaskResponsibleUserInfos.splice(responsibleUserIndex, 1);
        }
    }

    /**
     * Triggered when a user gets removed from our responsible list
     * @param index
     */
    onUserRemovedFromResponsibleList(index: number): void {
        // remove element from our list
        this.currentTaskResponsibleUserInfos.splice(index, 1);
    }

    /**
     * Triggered when the user wants to change the date via datepicker
     * @param datePicker
     */
    onDateItemClicked(datePicker: MatDatepicker<any>): void {
        if (!this.settingsSrv.currentUserRights.canManageTasks || this.currentTaskViewMode === 'view') {
            return;
        }

        datePicker.open();
    }

    /**
     * Triggered when a datepicker is closed
     * @param datePicker
     * @param datePickerType
     */
    onDatePickerClosed(datePicker: MatDatepicker<any>, datePickerType: 'max' | 'min') {
        if (datePickerType === 'min') {
            this.minTimestampDatePickerValue = ((datePicker.datepickerInput as any).value as moment.Moment)?.startOf('day');
            if (
                this.maxTimestampDatePickerValue != null &&
                this.maxTimestampDatePickerValue.isBefore(this.minTimestampDatePickerValue, 'day')
            ) {
                // if our current max timestamp is before our new min-timestamp we need to remove the max timestamp
                this.maxTimestampDatePickerValue = null;
            }
        } else if (datePickerType === 'max') {
            this.maxTimestampDatePickerValue = ((datePicker.datepickerInput as any).value as moment.Moment)?.endOf('day');
            if (
                this.minTimestampDatePickerValue != null &&
                this.minTimestampDatePickerValue.isAfter(this.maxTimestampDatePickerValue, 'day')
            ) {
                // if our current min timestamp is after our new max-timestamp we need to remove the min timestamp
                this.minTimestampDatePickerValue = null;
            }
        }
    }

    /**
     * Reset picker
     */
    resetPicker(event, datePickerType: 'max' | 'min') {
        event.stopPropagation();

        if (datePickerType === 'max') {
            this.maxTimestampDatePickerValue = null;
        } else if (datePickerType === 'min') {
            this.minTimestampDatePickerValue = null;
        }
    }

    /**
     * Display user infos for our list
     * @param user
     */
    displayUserInfos(user: User | string) {
        let userInfo: string;
        if (typeof user === 'string') {
            userInfo = user;
        } else {
            userInfo = user.firstName + ' ' + user.lastName;
            if (userInfo == null || userInfo.trim() === '') {
                userInfo += `${user.userName}`;
            } else {
                userInfo += ` (${user.userName})`;
            }
        }
        return userInfo;
    }

    /**
     * Sort user list by our custom sort
     * @param a the user to compare
     * @param b the user to compare
     */
    sortUserList(a: User | string, b: User | string): number {
        let aName = (typeof a === 'string' ? a : a?.firstName) + ' ' + (typeof a === 'string' ? a : a?.lastName);
        let bName = (typeof b === 'string' ? b : b?.firstName) + ' ' + (typeof b === 'string' ? b : b?.lastName);
        return aName.localeCompare(bName);
    }

    /**
     * Sort job list by our custom sort
     * @param a the job to compare
     * @param b the job to compare
     */
    sortJobList(a: BasicJobInfo, b: BasicJobInfo): number {
        if (b.sortOrder !== a.sortOrder) {
            return b.sortOrder > a.sortOrder ? 1 : -1;
        }
        return b.name.localeCompare(a.name);
    }

    /**
     * Triggered when the user wants to view a detailed job
     */
    async onViewDetailedJob(jobId: string) {
        // load sg from db
        try {
            let job = this.getJobDataById(jobId);
            this.detailedJobModalJobData = job;
            this.detailedJobModalLoading = true;
            let sgData = new ServiceGroup(await lastValueFrom(this.db.getItemById(job.serviceGroupId)));
            let project = new Project(await lastValueFrom(this.db.getItemById(job.projectId)));

            if (!this.projectView) {
                await this.projectSrv.setCurrentProject(project);
            } else {
                this.projectSrv.setCurrentServiceGroup(sgData._id);
            }

            this.detailedJobModalServiceGroupData = sgData;
            this.detailedJobModalProjectData = project;
            this.detailedJobModalLoading = false;

            if (!this.utils.isPhone) {
                this.detailedJobModalVisible = true;
                return;
            }

            await this.presentDetailedJobInModal();
        } catch (err) {
            this.detailedJobModalServiceGroupData = null;
            this.detailedJobModalProjectData = null;
            this.detailedJobModalJobData = null;
            this.detailedJobModalVisible = false;
            this.detailedJobModalLoading = false;

            this.notify.error(this.translate.instant('projects.jobs.errors.load-job-err'));
            this.logger.error('[TASKS PAGE] error while open job modal', err);
            this.utils.sentryCaptureMessage('error while open job modal for tasks', err);
        }
    }

    /**
     * Present detailed job modal for phone
     */
    async presentDetailedJobInModal(): Promise<void> {
        let jobModal = await this.modalCtrl.create({
            component: JobDetailsComponent,
            componentProps: {
                job: this.detailedJobModalJobData,
                project: this.detailedJobModalProjectData,
                serviceGroup: this.detailedJobModalServiceGroupData,
                taskId: this.currentTaskFormGroup.get('_id').value,
                subView: true,
                asDialog: true, // not really a dialog but activates the "dialog" ui version
                presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
            },
            animated: true,
            handle: false,
            backdropDismiss: true,
            backdropBreakpoint: 1,
            initialBreakpoint: 1,
            breakpoints: [0, 1],
            canDismiss: async (data?: any, role?: string) => {
                // prevent closing modal from swiping down -> this prevents a recent bug in ionic we have which cancels user activity
                return role !== 'gesture';
            },
        });
        await jobModal.present();

        // Wait and handle
        await jobModal.onWillDismiss();

        await this.detailedJobModalDismissed();
    }

    /**
     * Returns the job data from our current task jobs array
     * @param jobId
     */
    getJobDataById(jobId: string) {
        return this.currentTaskJobInfos.find((x) => x._id === jobId) as Job;
    }

    /**
     * Function checks if given licenseType can see all tasks
     * @param licenseType the name of the licenseType
     */
    canSeeAllTasks(licenseType: string) {
        let canSeeAllTasks = false;

        let foundLicenseType = this.authSrv.authInfo.companyData.availableLicenseTypes.find((x) => x.name === licenseType);
        if (foundLicenseType) {
            canSeeAllTasks = foundLicenseType.rights?.hasAccessToAllTasks;
        }

        return canSeeAllTasks;
    }

    /**
     * Triggered when user clicks on a job checkbox
     * @param isChecked is checkbox is checked
     */
    onJobCheckboxChanged(isChecked: boolean): void {
        if (isChecked) {
            const allDone = this.currentTaskJobsFormArray.every((x) => (x.get('done') as UntypedFormControl).value);
            if (allDone) {
                this.currentTaskFormGroup.get('workingStatus').setValue(TaskWorkingStatus.DONE);
            } else {
                let isMinOneItemDone = this.currentTaskJobsFormArray.some((x) => (x.get('done') as UntypedFormControl).value);
                if (isMinOneItemDone) {
                    this.currentTaskFormGroup.get('workingStatus').setValue(TaskWorkingStatus.STARTED);
                }
            }
        }
    }

    /**
     * Triggered when detailed job modal gets dismissed
     */
    async detailedJobModalDismissed(): Promise<void> {
        if (!this.projectView) {
            await this.projectSrv.setCurrentProject(null);
        }

        this.resetJobDisplayData();
    }

    /**
     * Reset job display data (after closing modal or saving job)
     */
    resetJobDisplayData() {
        this.detailedJobModalServiceGroupData = null;
        this.detailedJobModalProjectData = null;
        this.detailedJobModalJobData = null;
        this.detailedJobModalVisible = false;
        this.detailedJobModalLoading = false;
    }

    /**
     * Triggered when a user clicks to select a project
     */
    openProjectSelectionModal() {
        if (this.disabledProjectSelection || !this.settingsSrv.currentUserRights.canManageTasks || this.currentTaskViewMode === 'view') {
            return;
        }
        this.projectModalVisible = true;
    }

    /**
     * Called if status changes from popover
     * @param status (open, started, done)
     */
    changeStatus(status: string) {
        let isValid = true;
        if (status === TaskWorkingStatus.DONE) {
            const jobsArray = this.currentTaskFormGroup.get('jobs') as FormArray;

            // check if there are jobs and all are done
            if (jobsArray && jobsArray.length > 0) {
                const allDone = jobsArray.controls.every((control) => {
                    return control.get('done')?.value === true;
                });

                if (!allDone) {
                    isValid = false;
                    this.notify.warn(this.translate.instant('tasks.info.not-all-jobs-done'));
                }
            }
        }

        // only execute if its valid
        if (isValid) {
            this.currentTaskFormGroup.get('workingStatus').setValue(status);
            this.statusPopover.dismiss();
        }
    }
}
